import { useMemo, useState } from "react";

import {
  useGetCampaignComparisonJobQuery,
  useStartCampaignComparisonQuery,
} from "@/services/campaign-comparison";
import type { CampaignType } from "src/@types/campaign-propagation";
import type {
  IComparisonPlannedStore,
  IComparisonSummary,
  IStoreComparison,
} from "src/@types/campaign-comparison";

import type { ComparisonRow } from "../components/columns";
import type { FilterStateCampaignComparison } from "../filter";

function makePendingRow(ps: IComparisonPlannedStore): ComparisonRow {
  return {
    storeCode: ps.storeCode,
    storeName: ps.storeName,
    city: ps.city,
    uf: ps.uf,
    comparisonStatus: "SYNCED",
    propagationTracked: true,
    propagation: {
      status: "PENDING" as IStoreComparison["propagation"]["status"],
      attemptCount: 0,
      retryCount: 0,
      lastAttempt: null,
      lastAttemptAt: null,
      errorMessage: null,
    },
    storeData: null,
    divergences: [],
    rawComparison: [],
    scheduledJob: null,
    checkedAt: "",
    __loading: true,
  };
}

function buildPartialSummary(
  stores: IStoreComparison[],
  pageTotal: number,
  globalTotal: number,
): IComparisonSummary {
  const countBy = (status: IStoreComparison["comparisonStatus"]) =>
    stores.filter((s) => s.comparisonStatus === status).length;
  return {
    pageStores: pageTotal,
    totalStores: globalTotal,
    synced: countBy("SYNCED"),
    divergent: countBy("DIVERGENT"),
    notFoundInStore: countBy("NOT_FOUND_IN_STORE"),
    unreachable: countBy("UNREACHABLE"),
    propagationPendingOrFailed: 0,
    scheduledJobs: 0,
    allSyncedInPage: false,
    durationMs: 0,
  };
}

interface UseCampaignComparisonParams {
  validType?: CampaignType;
  numericCode?: number;
  filters: FilterStateCampaignComparison;
}

export function useCampaignComparison({
  validType,
  numericCode,
  filters,
}: UseCampaignComparisonParams) {
  const [bustByPage, setBustByPage] = useState<Record<string, number>>({});

  const ready = !!validType && !!numericCode;
  const pageKey = ready
    ? `${validType}-${numericCode}-${filters.page}-${filters.limit}`
    : "";
  const bust = bustByPage[pageKey] ?? 0;

  const start = useStartCampaignComparisonQuery(
    {
      type: validType as CampaignType,
      code: numericCode as number,
      page: filters.page,
      perPage: filters.limit,
      force: bust > 0,
      bust,
    },
    { skip: !ready },
  );
  const jobId = start.data?.jobId;

  const job = useGetCampaignComparisonJobQuery(jobId ?? "", { skip: !jobId });
  const jobStatus = job.currentData;

  const result = jobStatus?.result ?? null;
  const progress = jobStatus?.progress ?? null;
  const isTerminal =
    jobStatus?.state === "completed" || jobStatus?.state === "failed";
  const isProcessing =
    ready &&
    !result &&
    (start.isLoading || start.isFetching || (!!jobId && !isTerminal));
  const error = start.error || job.error || jobStatus?.failedReason;
  const retaguarda = result?.retaguarda ?? progress?.retaguarda ?? null;

  const rows = useMemo<ComparisonRow[]>(() => {
    if (result) return result.data as ComparisonRow[];
    if (progress) {
      const processed = new Map(
        progress.stores.map((s) => [s.storeCode, s as ComparisonRow]),
      );
      return (progress.plannedStores ?? []).map(
        (ps) => processed.get(ps.storeCode) ?? makePendingRow(ps),
      );
    }
    return [];
  }, [result, progress]);

  const filteredRows = useMemo<ComparisonRow[]>(() => {
    const term = filters.search.trim().toLowerCase();
    return rows.filter((r) => {
      if (filters.status !== "ALL") {
        if (r.__loading || r.comparisonStatus !== filters.status) return false;
      }
      if (term) {
        const hay = `${r.storeCode} ${r.storeName ?? ""}`.toLowerCase();
        if (!hay.includes(term)) return false;
      }
      return true;
    });
  }, [rows, filters.search, filters.status]);

  const summary =
    result?.summary ??
    (progress
      ? buildPartialSummary(
          progress.stores,
          progress.total,
          progress.meta?.total ?? progress.total,
        )
      : null);

  const hasItemComparison = !!(
    result?.data.some((s) => s.itemComparison) ||
    progress?.stores.some((s) => s.itemComparison)
  );

  const pagination = {
    page:
      result?.meta?.currentPage ?? progress?.meta?.currentPage ?? filters.page,
    limit: result?.meta?.perPage ?? progress?.meta?.perPage ?? filters.limit,
    total: result?.meta?.total ?? progress?.meta?.total ?? 0,
    totalPages: result?.meta?.lastPage ?? progress?.meta?.lastPage ?? 1,
  };

  const refresh = () => {
    if (!pageKey) return;
    setBustByPage((m) => ({ ...m, [pageKey]: (m[pageKey] ?? 0) + 1 }));
  };

  return {
    result,
    progress,
    retaguarda,
    summary,
    rows,
    filteredRows,
    pagination,
    hasItemComparison,
    isProcessing,
    error,
    refresh,
  };
}
